import * as React from 'react';
import { ChevronDown, MoonStar, Sun } from 'lucide-react';

import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { DynamicText } from '@/components/dynamic-text';
import { useTheme } from '@/components/theme-provider';
import {
  DEFAULT_VIEW,
  SCREENSHOT_VIEWS,
  screenshotAlt,
  screenshotSrc,
  type ScreenshotMode,
  type ScreenshotViewId,
} from '@/lib/screenshots';

/**
 * Two *independent* toggles, both plain local state:
 *   1. a light/dark Switch that swaps the screenshot variant, and
 *   2. a ToggleGroup that swaps which app view is shown. Below `md` the six tabs
 *      overflow a phone's width, so a native select stands in for it (BRA-138).
 * The Switch seeds from the site theme once, then goes its own way — you can
 * browse the dark screenshots on the light site and vice versa.
 */
export function ScreenshotShowcase() {
  const { theme } = useTheme();
  const [view, setView] = React.useState<ScreenshotViewId>(DEFAULT_VIEW);
  const [mode, setMode] = React.useState<ScreenshotMode>(theme);

  const active = SCREENSHOT_VIEWS.find((v) => v.id === view) ?? SCREENSHOT_VIEWS[0];
  const isDark = mode === 'dark';

  return (
    <section id="screens" className="container scroll-mt-24 py-20 sm:py-28">
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="kicker">
            <DynamicText path="screens.kicker">Features</DynamicText>
          </p>
          <h2 className="mt-3 max-w-lg text-[clamp(2rem,4.5vw,3rem)] leading-[1.05]">
            <DynamicText path="screens.title">Built for one hand, at 3am, in the dark.</DynamicText>
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <div className="relative w-full md:hidden">
            <select
              value={view}
              onChange={(e) => setView(e.target.value as ScreenshotViewId)}
              aria-label="Choose which app screen to preview"
              className="h-10 w-full appearance-none rounded-full border border-border bg-secondary/60 pl-4 pr-10 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {SCREENSHOT_VIEWS.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
          </div>

          <ToggleGroup
            className="hidden md:inline-flex"
            type="single"
            value={view}
            onValueChange={(next) => {
              // Radix emits '' when the active item is re-clicked; keep a selection.
              if (next) setView(next as ScreenshotViewId);
            }}
            aria-label="Choose which app screen to preview"
          >
            {SCREENSHOT_VIEWS.map((v) => (
              <ToggleGroupItem key={v.id} value={v.id} aria-label={`Show the ${v.label} screen`}>
                {v.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          <div className="flex items-center gap-2.5">
            <Sun aria-hidden="true" className="size-4 text-muted-foreground" />
            <Switch
              id="screenshot-appearance"
              checked={isDark}
              onCheckedChange={(checked) => setMode(checked ? 'dark' : 'light')}
              aria-label="Show dark appearance screenshots"
            />
            <MoonStar aria-hidden="true" className="size-4 text-muted-foreground" />
            <span className="sr-only" role="status">
              Showing {mode} appearance
            </span>
          </div>
        </div>
      </div>

      <div className="mt-14 grid gap-12 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-center">
        <div className="relative flex justify-center">
          {/* Soft berry glow behind the device */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-8 top-10 bottom-10 rounded-[999px] bg-berry/15 blur-3xl"
          />
          <DeviceFrame>
            <img
              key={`${view}-${mode}`}
              src={screenshotSrc(view, mode)}
              alt={screenshotAlt(view, mode)}
              width={390}
              height={844}
              loading="lazy"
              decoding="async"
              className="block h-full w-full animate-rise object-cover"
            />
          </DeviceFrame>
        </div>

        <figure className="relative lg:pl-8">
          <span
            aria-hidden="true"
            className="absolute left-0 top-1 hidden h-[calc(100%-0.5rem)] w-px bg-border lg:block"
          />
          <figcaption>
            <p className="font-display text-2xl leading-tight">
              <DynamicText path={`screens.${active.id}.label`}>{active.label}</DynamicText>
            </p>
            <p className="mt-3 text-muted-foreground">
              <DynamicText path={`screens.${active.id}.caption`}>{active.caption}</DynamicText>
            </p>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function DeviceFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-[15.5rem] sm:w-[17.5rem]">
      <div className="rounded-[2.6rem] border border-border bg-card p-2 shadow-[0_30px_60px_-25px_hsl(var(--foreground)/0.45)]">
        <div className="relative overflow-hidden rounded-[2.1rem] bg-background">
          <div
            aria-hidden="true"
            className="absolute left-1/2 top-2 z-10 h-5 w-20 -translate-x-1/2 rounded-full bg-foreground/85"
          />
          {children}
        </div>
      </div>
    </div>
  );
}
